import { engeniApi } from '@/api/engeni.api';
import type { Account } from '@/interfaces/account.interface';
import type { AccountBillingInformation } from '@/interfaces/accountBillingInformation.interface';
import type {
  ApiResponse,
  ApiResponseData,
  ApiResponsePaginatedV2,
} from '@/interfaces/api.interface';
import type { Invoice } from '@/interfaces/invoice.interface';
import type { PaymentType } from '@/interfaces/paymentType.interface';
import type { Plan } from '@/interfaces/plan.interface';
import type { Language, User } from '@/interfaces/user.interface';
import type {
  CalculateCustomPlanResponse,
  CreateAccountDTO,
  CreateAccountOrderDTO,
  CreateCartDTO,
  CreateOrderPlanChangeDTO,
  UpdateAccountDTO,
  UpdateCartDTO,
  UpdateUserDTO,
} from './cxm.service.interface';
import type { Order } from '@/interfaces/order.interface';
import type { Cart } from '@/interfaces/cart.interface';
import type { CountryDefaultHierarchySummary } from '@/interfaces/countryDefaultHierarchy.interface';
import type { Offer } from '@/interfaces/offer.interface';
import type { NpsPoll, RegisterNpsPollRequest } from '@/modules/nps/interfaces/nps.interface';
import type {
  DashboardFunnel,
  DashboardFunnelBySource,
  DashboardOverview,
  DashboardSourceBreakdown,
  DashboardTimeSeries,
  DashboardWhatsappBySource,
  DashboardWhatsappFunnel,
  DashboardWhatsappOverview,
  DashboardWhatsappTimeSeries,
  EcommerceDashboardFilters,
  EcommerceDashboardTimeSeriesFilters,
  EcommerceDashboardWhatsappTimeSeriesFilters,
} from '@/modules/flokee/interfaces/ecommerceDashboard.interface';
import { buildEcommerceDashboardParams } from '@/modules/flokee/utils/ecommerceDashboardParams.utils';
import type { OrderProductsOverview } from '@/interfaces/orderProductsOverview.interface';
import type { UserNotification } from '@/interfaces/userNotification.interface';
import type { OrderPlanChange } from '@/interfaces/orderPlanChange.interface';
import type { OrderSummaryResponse } from '@/interfaces/orderSummary.interface';
import type { PricingPage } from '@/interfaces/pricingPage.interface';
import type { CancellationReason } from '@/interfaces/cancellationReason.interface';
import { getBuConfigForContext } from '@/utils/bu.utils';
import { buildV2ListParams } from '@/utils/v2List.utils';

const BASE_PATH = '/cxm';
const BASE_PUBLIC_PATH = `/public${BASE_PATH}`;
const API_V2_BASE_PATH = '/cxm/v2';
const FLOK_V2_BASE_PATH = '/flok/v2';

export const cxmService = {
  async register(data: {
    name: string;
    email: string;
    password: string;
    timezone: string;
    language_id: number;
  }) {
    return await engeniApi.post<ApiResponseData<User>>(`${BASE_PUBLIC_PATH}/auth/register`, data);
  },
  async login(email: string, password: string) {
    return await engeniApi.post<ApiResponseData<{ access_token: string; user: User }>>(
      `${BASE_PUBLIC_PATH}/auth/login`,
      {
        email: email,
        password: password,
      },
    );
  },
  async tiendanubeLogin(code: string) {
    return await engeniApi.post<ApiResponseData<{ access_token: string; user: User }>>(
      `${BASE_PUBLIC_PATH}/auth/tiendanube/login`,
      {
        code,
      },
    );
  },
  async googleLogin(token: string) {
    return await engeniApi.post<ApiResponseData<{ access_token: string; user: User }>>(
      `${BASE_PUBLIC_PATH}/auth/google/login`,
      {
        token,
      },
    );
  },
  async googleOneTapLogin(credential: string) {
    return await engeniApi.post<ApiResponseData<{ access_token: string; user: User }>>(
      `${BASE_PUBLIC_PATH}/auth/google/one-tap`,
      {
        credential,
      },
    );
  },
  async logout() {
    return await engeniApi.post<ApiResponse>(`${BASE_PATH}/auth/logout`);
  },
  async forgotPassword(email: string, reset_link?: string) {
    const buConfig = getBuConfigForContext({
      path_value: window.location.pathname,
      origin_value: window.location.origin,
      use_stored: true,
    });
    const authBasePath = buConfig?.basePath ?? '';
    const defaultResetLink = `${window.location.origin}${authBasePath}/auth/reset-password`;

    return await engeniApi.post<ApiResponseData<{ message: string }>>(
      `${BASE_PUBLIC_PATH}/auth/forgot?reset_link=${reset_link ?? defaultResetLink}`,
      {
        email: email,
      },
    );
  },
  async resetPassword(
    token: string,
    email: string,
    password: string,
    password_confirmation: string,
  ) {
    return await engeniApi.post<ApiResponseData<{ message: string }>>(
      `${BASE_PUBLIC_PATH}/auth/reset`,
      {
        token: token,
        email: email,
        password: password,
        password_confirmation: password_confirmation,
      },
    );
  },
  async getCurrentUser() {
    return await engeniApi.get<ApiResponseData<User>>(
      `${BASE_PATH}/auth/user?embed=accounts,currentAccount.owner,currentAccount.partner,currentAccount.partner.currency,currentAccount.country,currentOrder,role,language`,
    );
  },
  async updateUser(user_id: number, data: Partial<UpdateUserDTO>) {
    // Omit current_account_id and current_order_id from payload (must not be sent to API)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally omitted from payload
    const { current_account_id: _, current_order_id: __, ...payload } = data ?? {};
    return await engeniApi.put<ApiResponse>(`${API_V2_BASE_PATH}/users/${user_id}`, payload);
  },
  async setPassword(user_id: number, data: { password: string; password_confirmation: string }) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/users/${user_id}/set-password`,
      data,
    );
  },
  async getUsers(
    filter?: {
      name?: string;
      email?: string;
      role_id?: number;
      role_id_is_in?: number[];
      current_account_id?: number;
      current_order_id?: number;
      active?: boolean;
    },
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
    sort?: string,
    embed?: string,
  ) {
    const params = buildV2ListParams({
      filter,
      pagination,
      sort,
      embed,
    });

    return await engeniApi.get<ApiResponsePaginatedV2<User[]>>(`${API_V2_BASE_PATH}/users`, {
      params,
    });
  },
  async getUser(user_id: number, embed?: string) {
    const params: Record<string, any> = {};
    if (embed) {
      params.embed = embed;
    }
    return await engeniApi.get<ApiResponseData<User>>(`${API_V2_BASE_PATH}/users/${user_id}`, {
      params,
    });
  },
  async createUser(data: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    language_id?: number;
    active?: boolean;
    role_id?: number;
    timezone?: string;
  }) {
    return await engeniApi.post<ApiResponseData<User>>(`${API_V2_BASE_PATH}/users`, data);
  },
  async deleteUser(user_id: number) {
    return await engeniApi.delete<ApiResponse>(`${API_V2_BASE_PATH}/users/${user_id}`);
  },
  async unsetCurrentAccount(user_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/users/${user_id}/unset-current-account`,
    );
  },
  async getUserAccounts(
    user_id: number,
    filter?: {
      name?: string;
      id?: number;
      active?: boolean;
    },
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
    sort?: string,
  ) {
    const params = buildV2ListParams({
      filter,
      pagination,
      sort,
    });

    return await engeniApi.get<ApiResponsePaginatedV2<Account[]>>(
      `${API_V2_BASE_PATH}/users/${user_id}/accounts`,
      {
        params,
      },
    );
  },
  async linkUserToAccount(user_id: number, data: { account_id: number; role_id: number }) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/users/${user_id}/link-to-account`,
      data,
    );
  },
  async getUserCuSupInfo(user_id: number) {
    return await engeniApi.get<ApiResponseData<any>>(
      `${API_V2_BASE_PATH}/users/${user_id}/cusup-info`,
    );
  },
  async updateUserCuSupInfo(
    user_id: number,
    data: {
      name?: string;
      whatsapp?: string;
      working_hours?: string;
      phone?: string;
      email?: string;
      avatar?: File | string;
    },
  ) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === 'avatar' && value instanceof File) {
          formData.append(key, value);
        } else if (value !== null) {
          formData.append(key, String(value));
        }
      }
    });
    return await engeniApi.put<ApiResponseData<any>>(
      `${API_V2_BASE_PATH}/users/${user_id}/cusup-info`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  },
  async getOrders(
    filter?: {
      id?: number;
      account_id?: number;
      status?: string | string[];
      total?: number;
      created_by?: number;
      product_reference_id?: number;
    },
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
    sort?: string,
    embed?: string,
  ) {
    const params = buildV2ListParams({
      filter,
      pagination,
      sort: sort ?? '-id',
      embed,
    });

    return await engeniApi.get<ApiResponsePaginatedV2<Order[]>>(`${API_V2_BASE_PATH}/orders`, {
      params,
    });
  },
  async getOrdersByAccountId(account_id: number) {
    const params = buildV2ListParams({
      filter: { account_id },
      pagination: { no_pagination: true },
      sort: '-id',
    });

    return await engeniApi.get<ApiResponsePaginatedV2<Order[]>>(`${API_V2_BASE_PATH}/orders`, {
      params,
    });
  },
  async getOrderById(order_id: number) {
    return await engeniApi.get<ApiResponseData<Order>>(`${API_V2_BASE_PATH}/orders/${order_id}`);
  },
  async getOrderProductsOverview(order_id: number) {
    return await engeniApi.get<ApiResponseData<OrderProductsOverview>>(
      `${API_V2_BASE_PATH}/orders/${order_id}/products-overview`,
    );
  },
  async createOrder(data: CreateAccountOrderDTO) {
    let offer_id = data.offer_id;

    if (!offer_id) {
      const plan_id = data.plan_ids?.[0];
      if (!plan_id) {
        throw new Error('createOrder requires offer_id or plan_ids');
      }

      const { data: offersResponse } = await this.getOffers(
        { plan_id },
        { no_pagination: true },
        '-id',
      );

      offer_id = offersResponse.data[0]?.id;
      if (!offer_id) {
        throw new Error('No offer found for plan_id');
      }
    }

    return await engeniApi.post<ApiResponseData<Order>>(`${API_V2_BASE_PATH}/orders`, {
      account_id: data.account_id,
      offer_id,
      name: data.name,
      logo: data.logo,
    });
  },
  async setCurrentOrder(user_id: number, order_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/users/${user_id}/set-current-order`,
      {
        order_id,
      },
    );
  },
  async createOrderPlanChange(order_id: number, data: CreateOrderPlanChangeDTO) {
    return await engeniApi.post<ApiResponseData<OrderPlanChange>>(
      `${API_V2_BASE_PATH}/order-plan-changes`,
      {
        ...data,
        order_id,
      },
    );
  },
  async cancelOrderPlanChange(order_id: number, order_plan_change_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/orders/${order_id}/plan-changes/${order_plan_change_id}/cancel`,
    );
  },
  async getOrderPlanChanges(
    order_id: number,
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
    sort?: string,
    embed?: string,
  ) {
    const params = buildV2ListParams({
      filter: { order_id },
      pagination,
      sort,
      embed,
    });

    return await engeniApi.get<ApiResponsePaginatedV2<OrderPlanChange[]>>(
      `${API_V2_BASE_PATH}/order-plan-changes`,
      { params },
    );
  },
  async getOpenedOrderPlanChange(order_id: number, embed?: string) {
    const params = embed ? { embed } : undefined;
    return await engeniApi.get<ApiResponseData<OrderPlanChange>>(
      `${API_V2_BASE_PATH}/orders/${order_id}/plan-changes/opened`,
      { params },
    );
  },
  async getOrderPlanChangePreview(order_id: number, data: CreateOrderPlanChangeDTO) {
    return await engeniApi.post<ApiResponseData<OrderPlanChange>>(
      `${API_V2_BASE_PATH}/orders/${order_id}/plan-changes/preview`,
      data,
    );
  },
  async getOrderSummary(order_id: number) {
    return await engeniApi.get<ApiResponseData<OrderSummaryResponse>>(
      `${API_V2_BASE_PATH}/orders/${order_id}/summary`,
    );
  },
  async getPricingPage(partner_id: number, solution_id: number) {
    return await engeniApi.get<ApiResponseData<PricingPage>>(
      `${API_V2_BASE_PATH}/partners/${partner_id}/solutions/${solution_id}/pricing-page`,
      {
        params: {
          embed: 'options.plan',
        },
      },
    );
  },
  async getAccounts(
    filter?: Record<string, any>,
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
  ) {
    const params = buildV2ListParams({
      filter,
      pagination,
      sort: '-id',
      embed: 'partner,owner,country',
    });

    return await engeniApi.get<ApiResponsePaginatedV2<Account[]>>(`${API_V2_BASE_PATH}/accounts`, {
      params,
    });
  },
  async createAccount(data: CreateAccountDTO) {
    return await engeniApi.post<ApiResponseData<Account>>(
      `${API_V2_BASE_PATH}/accounts/create`,
      data,
    );
  },
  async setCurrentAccount(user_id: number, account_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/users/${user_id}/set-current-account`,
      {
        account_id,
      },
    );
  },
  async updateAccount(account_id: number, data: Partial<UpdateAccountDTO>) {
    const payload = {
      ...data,
      logo: data.logo === '' ? null : data.logo,
    };

    return await engeniApi.put<ApiResponseData<Account>>(
      `${API_V2_BASE_PATH}/accounts/${account_id}`,
      payload,
    );
  },
  async getAccountUsers(
    account_id: number,
    filter?: {
      name?: string;
      email?: string;
      role_id?: number;
    },
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
    sort?: string,
    embed?: string,
  ) {
    const params = buildV2ListParams({
      pagination: {
        ...(pagination ?? {}),
        no_pagination: pagination?.no_pagination ?? true,
      },
      filter,
      sort,
      embed: embed ?? 'role',
    });

    return await engeniApi.get<ApiResponseData<User[]>>(
      `${API_V2_BASE_PATH}/accounts/${account_id}/users`,
      {
        params,
      },
    );
  },
  async deleteAccountUser(account_id: number, user_id: number) {
    return await engeniApi.delete<ApiResponse>(
      `${API_V2_BASE_PATH}/users/${user_id}/accounts/${account_id}`,
    );
  },

  async getAccountBillingInformation(
    account_id: number,
    filter?: {
      invoice_method_name?: string;
      invoice_method_id?: number;
    },
    sort?: string,
    embed?: string,
  ) {
    const params = buildV2ListParams({
      filter: {
        account_id: account_id,
        ...(filter ?? {}),
      },
      sort,
      embed,
    });

    const { data: response } = await engeniApi.get<ApiResponseData<AccountBillingInformation[]>>(
      `${API_V2_BASE_PATH}/account-billing-information`,
      {
        params,
      },
    );

    // TanStack Query queryFns must not resolve to undefined.
    // When the API returns an empty list, expose null explicitly.
    return response.data[0] ?? null;
  },
  async getAccountInvoices(
    account_id: number,
    filter?: {
      payment_status?: string;
      invoice_status?: string;
      order_subscription_id?: number;
      currency_id?: number;
    },
    sort?: string,
    embed?: string,
  ) {
    return await engeniApi.get<ApiResponseData<Invoice[]>>(
      `${API_V2_BASE_PATH}/accounts/${account_id}/invoices`,
      {
        params: buildV2ListParams({
          filter,
          sort,
          embed: embed ?? 'orderSubscription',
          pagination: {
            no_pagination: true,
          },
        }),
      },
    );
  },
  async retryChargeInvoice(account_id: number, invoice_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/accounts/${account_id}/invoices/${invoice_id}/retry-charge`,
    );
  },
  async getAccountPaymentTypes(
    account_id: number,
    filter?: {
      type?: string;
      is_main?: boolean;
    },
    sort?: string,
    embed?: string,
  ) {
    return await engeniApi.get<ApiResponseData<PaymentType[]>>(
      `${API_V2_BASE_PATH}/accounts/${account_id}/payment-types`,
      {
        params: buildV2ListParams({
          filter,
          sort,
          embed,
          pagination: {
            no_pagination: true,
          },
        }),
      },
    );
  },
  async createPaymentType(account_id: number, data: Partial<PaymentType>) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit account_id from payload
    const { account_id: _, is_main: __, ...payload } = data ?? {};

    return await engeniApi.post<ApiResponseData<PaymentType>>(
      `${API_V2_BASE_PATH}/accounts/${account_id}/payment-types`,
      {
        ...payload,
        is_main: true,
      },
    );
  },
  async updatePaymentType(account_id: number, payment_type_id: number, data: Partial<PaymentType>) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit account_id from payload
    const { account_id: _, ...payload } = data ?? {};

    return await engeniApi.put<ApiResponseData<PaymentType>>(
      `${API_V2_BASE_PATH}/accounts/${account_id}/payment-types/${payment_type_id}`,
      payload,
    );
  },
  async deletePaymentType(account_id: number, payment_type_id: number) {
    return await engeniApi.delete<ApiResponseData<PaymentType>>(
      `${API_V2_BASE_PATH}/accounts/${account_id}/payment-types/${payment_type_id}`,
    );
  },
  async updateAccountBillingInformation(
    billing_information_id: number,
    data: Partial<AccountBillingInformation>,
  ) {
    return await engeniApi.put<ApiResponseData<PaymentType>>(
      `${API_V2_BASE_PATH}/account-billing-information/${billing_information_id}`,
      data,
    );
  },
  async getPlanById(plan_id: number) {
    return await engeniApi.get<ApiResponseData<Plan>>(`${API_V2_BASE_PATH}/plans/${plan_id}`, {
      params: {
        embed: 'currency',
      },
    });
  },
  async calculateCustomPlan(plan_id: number, payload: { total_cost: number }) {
    return await engeniApi.post<ApiResponseData<CalculateCustomPlanResponse>>(
      `${API_V2_BASE_PATH}/plans/${plan_id}/calculate-custom-plan`,
      {
        ...payload,
      },
    );
  },
  async getOrderPlans(order_id: number) {
    return await engeniApi.get<ApiResponseData<Order>>(
      `${API_V2_BASE_PATH}/orders/${order_id}/plans`,
    );
  },
  async contactSupport(data: { subject: string; message: string }) {
    return await engeniApi.post<ApiResponse>(`${API_V2_BASE_PATH}/contact-supports`, data);
  },
  async getCart(cart_id: number) {
    return await engeniApi.get<ApiResponseData<Cart>>(`${API_V2_BASE_PATH}/carts/${cart_id}`);
  },
  async createCart(data: CreateCartDTO) {
    return await engeniApi.post<ApiResponseData<Cart>>(`${API_V2_BASE_PATH}/carts`, data);
  },
  async updateCart(cart_id: number, data: UpdateCartDTO) {
    return await engeniApi.put<ApiResponseData<Cart>>(`${API_V2_BASE_PATH}/carts/${cart_id}`, data);
  },
  async checkoutCart(cart_id: number) {
    return await engeniApi.post<ApiResponse>(`${API_V2_BASE_PATH}/carts/${cart_id}/checkout`);
  },
  async validateCoupon(data: { code: string; new_plan_id: number }) {
    return await engeniApi.post<ApiResponse>(`${API_V2_BASE_PATH}/coupons/validate`, data);
  },
  async getLanguages(
    filter?: {
      name?: string;
      code?: string;
    },
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
    sort?: string,
  ) {
    return await engeniApi.get<ApiResponseData<Language[]>>(`${BASE_PUBLIC_PATH}/v2/languages`, {
      params: buildV2ListParams({
        filter,
        pagination,
        sort,
      }),
    });
  },
  async getCountryDefaultTrees(
    params: { business_unit?: string; country_id?: number; is_fallback?: boolean } = {},
  ) {
    return await engeniApi.get<ApiResponseData<CountryDefaultHierarchySummary[]>>(
      `${API_V2_BASE_PATH}/country-defaults/valid-trees`,
      {
        params: buildV2ListParams({
          filter: {
            business_unit: params.business_unit,
            country_id: params.country_id,
            is_fallback: params.is_fallback,
          },
          pagination: {
            no_pagination: true,
          },
          embed: 'country',
        }),
      },
    );
  },
  async getOfferBySlug(slug: string) {
    return await engeniApi.get<ApiResponseData<Offer>>(`${API_V2_BASE_PATH}/offers/${slug}`, {
      params: {
        embed: 'solution.partner.country',
      },
    });
  },
  async getEcommerceDashboardOverview(ecommerce_id: number, filters?: EcommerceDashboardFilters) {
    return await engeniApi.get<ApiResponseData<DashboardOverview>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/overview`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardFunnel(ecommerce_id: number, filters?: EcommerceDashboardFilters) {
    return await engeniApi.get<ApiResponseData<DashboardFunnel>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/funnel`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardFunnelBySource(
    ecommerce_id: number,
    filters?: EcommerceDashboardFilters,
  ) {
    return await engeniApi.get<ApiResponseData<DashboardFunnelBySource>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/funnel/by-source`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardSourceBreakdown(
    ecommerce_id: number,
    filters?: EcommerceDashboardFilters,
  ) {
    return await engeniApi.get<ApiResponseData<DashboardSourceBreakdown>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/source-breakdown`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardTimeSeries(
    ecommerce_id: number,
    filters?: EcommerceDashboardTimeSeriesFilters,
  ) {
    return await engeniApi.get<ApiResponseData<DashboardTimeSeries>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/time-series`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardWhatsappOverview(
    ecommerce_id: number,
    filters?: EcommerceDashboardFilters,
  ) {
    return await engeniApi.get<ApiResponseData<DashboardWhatsappOverview>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/whatsapp/overview`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardWhatsappFunnel(
    ecommerce_id: number,
    filters?: EcommerceDashboardFilters,
  ) {
    return await engeniApi.get<ApiResponseData<DashboardWhatsappFunnel>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/whatsapp/funnel`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardWhatsappBySource(
    ecommerce_id: number,
    filters?: EcommerceDashboardFilters,
  ) {
    return await engeniApi.get<ApiResponseData<DashboardWhatsappBySource>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/whatsapp/by-source`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getEcommerceDashboardWhatsappTimeSeries(
    ecommerce_id: number,
    filters?: EcommerceDashboardWhatsappTimeSeriesFilters,
  ) {
    return await engeniApi.get<ApiResponseData<DashboardWhatsappTimeSeries>>(
      `${FLOK_V2_BASE_PATH}/ecommerces/${ecommerce_id}/dashboard/whatsapp/time-series`,
      {
        params: buildEcommerceDashboardParams(filters),
      },
    );
  },
  async getOffers(
    filter?: {
      slug?: string;
      solution_name?: string;
      plan_name?: string;
      created_by_name?: string;
      created_by?: number;
      country_code?: string;
      solution_id?: number;
      plan_id?: number;
      partner_id?: number;
      business_unit?: string;
      id?: number;
    },
    pagination?: { page?: number; per_page?: number; no_pagination?: boolean },
    sort?: string,
    embed?: string,
  ) {
    const params = buildV2ListParams({
      filter,
      pagination,
      sort,
      embed,
    });

    return await engeniApi.get<ApiResponsePaginatedV2<Offer[]>>(`${API_V2_BASE_PATH}/offers`, {
      params,
    });
  },
  /**
   * Get NPS poll details by ID (public, no auth).
   * GET /public/cxm/v2/nps-polls/{nps_poll}
   * @param embed Optional comma-separated relations: account, order, user, plan, plan.solution
   * @throws 404 poll not found
   */
  async getNpsPoll(nps_poll: number, embed?: string) {
    const params = embed ? { embed } : undefined;
    return await engeniApi.get<ApiResponseData<NpsPoll>>(
      `${BASE_PUBLIC_PATH}/v2/nps-polls/${nps_poll}`,
      { params },
    );
  },
  /**
   * Register NPS poll response (public, no auth).
   * POST /public/cxm/v2/nps-polls/{nps_poll}/register
   * @returns 204 No Content on success
   * @throws 404 poll not found, 409 already answered, 422 validation error
   */
  async registerNpsPoll(nps_poll: number, data: RegisterNpsPollRequest): Promise<void> {
    await engeniApi.post<void>(`${BASE_PUBLIC_PATH}/v2/nps-polls/${nps_poll}/register`, data);
  },
  async getNotifications(
    filter?: {
      type?: string;
      unread?: boolean;
    },
    sort?: string,
  ) {
    return await engeniApi.get<ApiResponseData<UserNotification[]>>(
      `${API_V2_BASE_PATH}/notifications`,
      {
        params: buildV2ListParams({
          filter,
          sort,
          pagination: {
            no_pagination: true,
          },
        }),
      },
    );
  },
  async deleteNotification(notification_id: string) {
    return await engeniApi.delete<ApiResponse>(
      `${API_V2_BASE_PATH}/notifications/${notification_id}`,
    );
  },
  async markNotificationAsRead(notification_id: string) {
    return await engeniApi.post<ApiResponse>(
      `${API_V2_BASE_PATH}/notifications/${notification_id}/read`,
    );
  },
  async markAllNotificationsAsRead() {
    return await engeniApi.post<ApiResponse>(`${API_V2_BASE_PATH}/notifications/read-all`);
  },
  async getCancellationReasons(
    filter?: {
      public?: boolean;
      name?: string;
    },
    sort?: string,
  ) {
    return await engeniApi.get<ApiResponseData<CancellationReason[]>>(
      `${API_V2_BASE_PATH}/cancellation-reasons`,
      {
        params: buildV2ListParams({
          filter,
          sort,
        }),
      },
    );
  },
};
